import * as THREE from 'three';
import { REEF_ISLANDS } from '../sim/reef';
import { COASTLINE_KM, TOWED_TURTLES_PER_1000_SEGMENTS, carapaceCm, classAgeYears, mauiNuiWeights, stagesOn, type TurtleLife } from '../sim/turtles';
import { HONU_CARAPACE_PX, HONU_FRAMES, HONU_SPRITE, type HonuFrame } from '../ui/data/honuSprites';
import { FishBatch, facesRight, fishAtlas, nearFade, type FishDraw } from './fishBatch';
import { hash3 } from './fishSwim';

/**
 * The green sea turtles, the honu, in the exhibit (M104, the redesign's section 5: "the reef's honu foraging by day,
 * resting by night"): the young and the adults at home at Maui Nui on the day, as the simulation counts them (M93,
 * `sim/turtles.ts`), a sprite for `HONU_DRAW.turtlesPerSprite` of each, each the size of the turtle in the middle of
 * its share, on its island's coast as the towed surveys found them (`honuIslandShares`); each resting on the bottom
 * by night and foraging in the shallows by day, the day's hours as Hawaiʻi's honu keep them (`HONU_DAY`). The adults
 * away breeding are not drawn. Drawn in one batch (`fishBatch.ts`), seen by the fish's light. The arithmetic is pure
 * and tested in node (`tests/honuDrawn.test.ts`).
 */

/**
 * How the honu are drawn (each a rendering choice, said so):
 *
 * - `turtlesPerSprite`: a sprite for 1,000 of the young and as many of the adults (JUDGED: about 14 young and 1 or 2
 *   adults at home on the first day).
 * - Each drawn to the fish's scale (`UNITS_PER_MM` at the exhibit's `EXHIBIT_SPRITES.scale`), its carapace its straight
 *   carapace length (`HONU_CARAPACE_PX`): the reef's animals to scale with each other, as the fish are (M75); the
 *   whales alone are drawn smaller than their scale (`WHALE_DRAW`).
 * - `gapUnits`: how far above the floor a honu is drawn, lying on it, by the highest of the floor under its length
 *   (JUDGED; M104's review: by the floor under its middle alone, 53 of 536 foraging frames cut into the slope).
 * - `wanderUnits`, `wanderS`: a forager's slow way along the coast, either side of its place, at the shallowest water
 *   that holds it all along, and seconds a round (JUDGED: how far a honu moves as it grazes is unread; M104's review:
 *   a wander out from the coast took some deeper than they rest).
 * - `fadeS`: seconds a honu takes to fade in or out as their numbers change (JUDGED, the whales').
 */
export const HONU_DRAW = { turtlesPerSprite: 1000, gapUnits: 0.03, wanderUnits: 0.3, wanderS: 40, fadeS: 3 } as const;

/**
 * The honu's stroke (M143, the user's word: "alternate frames based on the speed that the sprite will next travel. for
 * example, if it is moving quickly in one direction, have the frames alternate more quickly as well, no faster than 0.5
 * seconds per frame. the slowest alternating frame rate should be about 3 seconds per frame"): its two frames
 * (`HONU_FRAMES`), the two halves of its stroke, each held for the time it takes to swim half a stroke's way at the
 * speed it will swim over the next `aheadS` seconds, no less than `minS` and no more than `maxS`.
 *
 * - `lengthsPerStroke`: a stroke's way, body lengths, snout to tail: young green turtles in a flume at 25°C, about
 *   Maui Nui's water, swam at most 3.31 body lengths a second, their foreflippers beating 1.47 cycles a second (SD
 *   0.33, n = 4), the ratio of the two means (Davenport 1998, in NOAA Tech. Memo. NMFS-SEFSC-415, pp. 39 to 41, Table
 *   2; MEASURED there, the ratio derived). They were a month past hatching and at their fastest: how far a grown honu
 *   goes a stroke, and whether that holds as it slows (Booth 2014 found speed not correlated with stroke frequency in
 *   hatchlings, as Hoover et al. 2017 cite it, p. 4), are unread, their hosts refused.
 * - `bodyPx`: the honu's body length in its first frame, its snout at column 0 to its shell's rear tip at 140, its
 *   tail no farther (MEASURED off the sprite, the tip by M137's reading).
 * - `aheadS`: how far ahead its speed is read, seconds of the player's time (JUDGED: the shortest a frame is held).
 * - `restFrame`: the frame a honu lying at rest holds, its front flippers back along its sides: lying on the bottom
 *   it makes no strokes (immature green turtles resting on or under a ledge "either did not move or remained still and
 *   looked around", Okuyama et al. 2013, PLoS ONE 8(6): e65783, p. 9; loggerheads' "Flipper beats decreased
 *   substantially when turtles were either on the surface or on the bottom", Patel et al. 2016, Front. Mar. Sci.
 *   3:254, p. 7), and the second picture's flippers are the nearer a resting turtle's (JUDGED).
 */
export const HONU_STROKE = { lengthsPerStroke: 3.31 / 1.47, bodyPx: 141, aheadS: 0.5, minS: 0.5, maxS: 3, restFrame: 1 } as const;

/** The screen's right and up where no camera is given: the world's x and y, the whole tank's front view. */
const WORLD_AXES = { right: { x: 1, y: 0, z: 0 }, up: { x: 0, y: 1, z: 0 } } as const;

/**
 * Frame `f` of a honu's stroke drawn for a honu `size` (its first frame's) with its middle at `at`: its quad's foot
 * and size, its carapace the first frame's, laid where its shell's back lies along the first's (`HONU_FRAMES`' `at`),
 * mirrored with it facing right (M143). The quads stand toward the camera (`FishBatch`), so the offset runs along the
 * camera's right and up (`axes`; M143's review: along the world's x, the shell slipped 1.3 of the first frame's pixels
 * along and 0.8 up at each swap with the camera turned to its limit). Lying at rest on its second frame, its lowest
 * pixel stands 1.87 of the first frame's pixels above the first's, its gap over the floor (`gapUnits`) near doubled:
 * laid on the first frame's foot instead, it jumped that far each time it lay down or set off (M143's review; JUDGED,
 * a gap held still over a jump).
 */
export function frameQuad(f: number, size: { width: number; height: number }, at: { x: number; y: number; z: number }, right: boolean, axes: { right: { x: number; y: number; z: number }; up: { x: number; y: number; z: number } } = WORLD_AXES): { x: number; y: number; z: number; width: number; height: number } {
  const frame = HONU_FRAMES[f] as HonuFrame;
  const unit = size.width / HONU_SPRITE.width;
  const k = (HONU_CARAPACE_PX / frame.carapacePx) * unit;
  const [width, height] = [frame.sprite.width * k, frame.sprite.height * k];
  const [ox, oy] = [frame.at[0] * unit + width / 2 - size.width / 2, frame.at[1] * unit + height / 2 - size.height / 2];
  // Its foot from the first frame's: across by its offset, up by the two's halves apart less its offset down.
  const [across, up] = [right ? -ox : ox, size.height / 2 - height / 2 - oy];
  return {
    x: at.x + across * axes.right.x + up * axes.up.x,
    y: at.y - size.height / 2 + across * axes.right.y + up * axes.up.y,
    z: at.z + across * axes.right.z + up * axes.up.z,
    width,
    height,
  };
}

/** How long a honu holds a frame of its stroke, seconds, swimming `speed` body lengths a second (`HONU_STROKE`). */
export function strokeHoldS(speed: number): number {
  return Math.min(HONU_STROKE.maxS, Math.max(HONU_STROKE.minS, HONU_STROKE.lengthsPerStroke / 2 / Math.max(1e-9, speed)));
}

/**
 * The honu's day, as the sources give it (M104; each read where it is printed, the page in brackets the PDF's):
 *
 * - **Foraging by day in the shallows**: "most foraging occurs at between 0 and 2 M depth for an average of 8.9
 *   hours/day" (Rice et al., a dive recorder on one turtle at Punaluʻu for 226 days, NOAA Tech. Memo. NMFS-SEFSC-436,
 *   p. 231 [254]); drawn at the shallowest water that holds its sprite, all along its way: its middle 2.0 to 4.9 m
 *   down on the first day (MEASURED), the smallest young the shallowest.
 * - **Resting on the bottom at night**, Turtle 257's resting dives: "Resting dives showed a depth range from 4 to 38.5
 *   meters. The average depth of resting dives was 12.9 M. Dives deeper than 14 meters were uncommon and appeared to
 *   be exploratory dives" (ibid., p. 230 [253]): the exploratory dives are not a resting place, so each sprite rests
 *   from `restM`'s 4 m to `exploratoryM`'s 14 m, its depth a power of an even draw that gives the recorder's mean,
 *   `restMeanM` (the shape JUDGED; the mean the recorder's with its deep dives, M104's second review); never
 *   shallower than its way while foraging. Others rest shallower than Turtle 257: Kiholo's in a pond "where the
 *   maximum depth is less than four meters" (Balazs et al., ibid., p. 284 [307]), Kahului's at an outfall where "The
 *   depth at this site is only 2 m or less" (Balazs 1996, NMFS-SEFSC-387, p. 18); the one turtle's record is the
 *   game's, said on the card.
 * - **Leaving before sunrise**: "During the early morning, usually just before sunrise, the sonic-tagged turtles
 *   would leave the pond to feed" (Balazs et al., Kiholo, NMFS-SEFSC-436, p. 284 [307]); "82% of the turtles observed
 *   exiting the ponds left between 0500 hours and 0900 hours" (Harrington et al., Kiholo, NMFS-SEFSC-477, p. 286
 *   [291]): `leaveH`, each sprite's hour drawn evenly within it (all of them, JUDGED).
 * - **Coming back from the late afternoon into the night**: of the honu resting at Kahului's warm-water outfall,
 *   "They start to arrive in the late afternoon, and most leave before sunrise" (Balazs 1996, p. 18, read from the
 *   page's image); "The arrival of turtles near the outfall starting at sunset, and their increase after the end of
 *   twilight" (Balazs, Forsyth and Kam 1987, NOAA-TM-NMFS-SWFC-71, p. 24): each sprite's arrival drawn evenly from
 *   `backFromH` (the late afternoon, JUDGED from the words) to `afterDuskH` past the end of civil twilight
 *   (`civilDuskHour`; how long after it they keep coming is unread, JUDGED).
 * - **An hour's swim each way**: Turtle 257's day "approximately 12 hours of resting dives, 9 hours of foraging
 *   behavior, 1 hour of basking behavior and 2 hours of surface/travel time" (Rice et al., p. 231 [254]): `travelH`,
 *   its two hours split between the morning's swim and the evening's (JUDGED). "Within approximately 300 m at Puako
 *   and 500 m at Punalu'u" between resting and feeding (Davis et al., NMFS-SEFSC-443, p. 154 [172]); in the exhibit the
 *   way is as far as its floor puts the two depths apart, said on the card.
 * - The sources' hours are Hawaiʻi Standard Time's, of 150° W, at places near 156° W (Kiholo and Kahului, JUDGED from
 *   the map), where the clock runs `clockAheadH` ahead of the sun (6° at 15° an hour): taken off, as the exhibit's hour
 *   is the sun's.
 *
 * Not drawn: the night foraging some turtles do ("Seventy-two percent of foraging behavior in Turtle 257 occurred
 * between the hours of 0600 and 1800", Rice et al., p. 231 [254]; Puakō's T97 "9.6 hr ... during the day and 9.4 hr at
 * night", Davis et al., p. 154 [172]), and the basking.
 */
export const HONU_DAY = {
  restM: [4, 38.5],
  restMeanM: 12.9,
  exploratoryM: 14,
  leaveH: [5, 9],
  backFromH: 16,
  afterDuskH: 1,
  travelH: 1,
  clockAheadH: 0.4,
} as const;

/** The power that gives the resting depths, 4 to 14 m, the recorder's mean: 14 - 10 w^p over an even w has mean 14 - 10 / (p + 1). */
const REST_POWER = (HONU_DAY.exploratoryM - HONU_DAY.restM[0]) / (HONU_DAY.exploratoryM - HONU_DAY.restMeanM) - 1;

/** A resting depth, m, from an even draw `u` (0 to 1), rising with it: 4 m at 0, 14 m at 1. */
export function restDepthM(u: number): number {
  return HONU_DAY.exploratoryM - (HONU_DAY.exploratoryM - HONU_DAY.restM[0]) * (1 - u) ** REST_POWER;
}

/**
 * Each of Maui Nui's islands' share of its honu (in `REEF_ISLANDS` order): its turtles a tow segment times its
 * coastline, Kahoʻolawe's, not towed, at the other three's turtles a segment, as `mauiNuiShare` weighs them
 * (`mauiNuiWeights`).
 */
export function honuIslandShares(): number[] {
  const weight = mauiNuiWeights(TOWED_TURTLES_PER_1000_SEGMENTS, COASTLINE_KM);
  const all = REEF_ISLANDS.reduce((a, i) => a + weight[i], 0);
  return REEF_ISLANDS.map((i) => weight[i] / all);
}

/** A honu sprite: an adult or a young one, its place among its stage's sprites, its carapace, cm, and its island. */
export interface HonuSprite {
  adult: boolean;
  index: number;
  cm: number;
  island: number;
  /** Its age class (M203: a tag of the one tapped of its class). */
  cls?: number;
}

/**
 * The day's sprites from the honu at home by class (`TurtleState.home`): each stage's turtles over
 * `perSprite`, rounded; sprite k of n the class at the middle of its share, (k + ½) / n of the stage's turtles taken
 * youngest first, and that class's carapace on the day; its island by the islands' shares, at k's place in an even
 * spread over them (the golden ratio's, so each island keeps its sprites as their number changes).
 */
export function honuSprites(life: TurtleLife, home: readonly number[], epochDay: number, perSprite: number = HONU_DRAW.turtlesPerSprite): HonuSprite[] {
  const stages = stagesOn(life, epochDay);
  const shares = honuIslandShares();
  const out: HonuSprite[] = [];
  for (const adult of [false, true]) {
    const counts = home.map((n, c) => ((stages[c] === 'adult') === adult ? n : 0));
    const total = counts.reduce((a, n) => a + n, 0);
    const n = Math.round(total / perSprite);
    let [cls, below] = [0, 0];
    for (let k = 0; k < n; k++) {
      const at = ((k + 0.5) / n) * total;
      while (cls < counts.length - 1 && below + (counts[cls] as number) < at) below += counts[cls++] as number;
      let u = (k * 0.6180339887 + (adult ? 0.5 : 0.25)) % 1;
      let island = 0;
      while (island < shares.length - 1 && u >= (shares[island] as number)) u -= shares[island++] as number;
      out.push({ adult, index: k, cm: carapaceCm(life, classAgeYears(life, cls, epochDay)), island, cls });
    }
  }
  return out;
}

/** The tank as a honu's places are found in it: its x and z spans, the surface's height, its floor, a height's real depth, and a place front to back on an island's coast. */
export interface HonuWater {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  top: number;
  floorY: (x: number, z: number) => number;
  depthOf: (y: number) => number;
  zOf: (island: number, u: number) => number | null;
}

/** A place in the tank, across and front to back. */
export interface Spot {
  x: number;
  z: number;
}

/**
 * A honu's places: front to back, its foraging place (its middle's height there) and its way along the coast, each
 * point the shallowest water that holds it; where it rests (its middle); its resting depth; and its hours' draws.
 */
export interface HonuPlaces {
  z: number;
  forage: { x: number; y: number };
  shallows: readonly Spot[];
  rest: { x: number; y: number };
  restM: number;
  leave: number;
  back: number;
  wander: number;
}

/** Steps across the tank a honu's places are searched in, the halvings that then find its resting depth, and the points of its way along the coast (JUDGED). */
const PLACE_STEPS = 640;
const REST_HALVINGS = 16;
const WANDER_POINTS = 9;
/** Points along a honu's length its floor is read at, its ends a twentieth in (JUDGED). */
const FOOT_POINTS = 5;

/** A honu's middle lying on the floor at a place: by the highest of the floor under its length, `gapUnits` above it. */
function lyingY(floorY: (x: number, z: number) => number, x: number, z: number, size: { width: number; height: number }): number {
  let floor = -Infinity;
  for (let k = 0; k < FOOT_POINTS; k++) floor = Math.max(floor, floorY(x + (k / (FOOT_POINTS - 1) - 0.5) * 0.9 * size.width, z));
  return floor + size.height / 2 + HONU_DRAW.gapUnits;
}

/**
 * Sprite `key`'s places, the same for it however many are drawn: a place on its island's coast by its seed; from the
 * coast out, the first floor deep enough to hold its sprite lying on it (its foraging place), and so at each point of
 * its way along the coast, `wanderUnits` either side within the tank; and beyond its foraging place the first where its
 * middle, lying there, is as deep as its resting depth (the recorder's depth is the turtle's own), that never shallower
 * than its way. Null where its coast has no such floor.
 */
export function honuPlaces(key: number, island: number, water: HonuWater, size: { width: number; height: number }): HonuPlaces | null {
  const z = water.zOf(island, hash3(key, 1, 71));
  if (z === null) return null;
  const at = (i: number): number => water.x0 + (i / PLACE_STEPS) * (water.x1 - water.x0);
  const lying = (x: number, zz = z): number => lyingY(water.floorY, x, zz, size);
  const shallowest = (zz: number): number | null => {
    let f = 0;
    while (f <= PLACE_STEPS && lying(at(f), zz) + size.height / 2 > water.top) f++;
    return f > PLACE_STEPS ? null : f;
  };
  const f = shallowest(z);
  if (f === null) return null;
  const forage = { x: at(f), y: lying(at(f)) };
  const shallows: Spot[] = [];
  for (let k = 0; k < WANDER_POINTS; k++) {
    const zz = Math.max(water.z0, Math.min(water.z1, z + (2 * k / (WANDER_POINTS - 1) - 1) * HONU_DRAW.wanderUnits));
    const g = shallowest(zz);
    if (g !== null) shallows.push({ x: at(g), z: zz });
  }
  // Never shallower than any point of its way along the coast (M104's review: some foraged deeper than they rested).
  const restM = Math.max(restDepthM(hash3(key, 2, 71)), ...[forage, ...shallows].map((q) => water.depthOf(lying(q.x, 'z' in q ? q.z : z))));
  let r = f;
  while (r < PLACE_STEPS && water.depthOf(lying(at(r))) < restM) r++;
  // Halved down to its depth between the step before and this one (a steep floor drops meters a step).
  let [lo, hi] = [at(Math.max(f, r - 1)), at(r)];
  for (let i = 0; i < REST_HALVINGS && r > f && water.depthOf(lying(hi)) >= restM; i++) {
    const mid = (lo + hi) / 2;
    if (water.depthOf(lying(mid)) >= restM) hi = mid;
    else lo = mid;
  }
  return { z, forage, shallows, rest: { x: hi, y: lying(hi) }, restM, leave: hash3(key, 3, 71), back: hash3(key, 4, 71), wander: hash3(key, 5, 71) };
}

/** The hours, the sun's, a honu leaves its resting place and gets back to it, on a day whose civil twilight ends at `duskHour`. */
export function honuHours(p: HonuPlaces, duskHour: number): { leave: number; back: number } {
  const leave = HONU_DAY.leaveH[0] + p.leave * (HONU_DAY.leaveH[1] - HONU_DAY.leaveH[0]) - HONU_DAY.clockAheadH;
  const latest = duskHour + HONU_DAY.afterDuskH;
  const earliest = Math.min(latest, HONU_DAY.backFromH - HONU_DAY.clockAheadH);
  return { leave, back: earliest + p.back * (latest - earliest) };
}

export type HonuActivity = 'resting' | 'leaving' | 'foraging' | 'returning';

/** How far out from the coast a forager's place is moved, scene units a step, where its way's straight line between two points would break the surface (JUDGED). */
const SHALLOWS_STEP = 0.01;

/** How far back a foraging honu's facing looks for the way it moves, seconds of its swim (JUDGED: a tenth of a second, a few frames). */
const HONU_FACING_S = 0.1;

/**
 * A honu at an hour of the sun (0 to 24), `seconds` into the player's time: on the bottom at its resting place by
 * night; swimming to its foraging place over `travelH` from its hour of leaving, eased at each end; going slowly along
 * the coast there, at the shallowest water that holds it all along; and back, to arrive at its hour. Kept above the
 * floor on the way.
 */
export function honuAt(p: HonuPlaces, hour: number, duskHour: number, seconds: number, floorY: (x: number, z: number) => number, size: { width: number; height: number }, top: number): { x: number; y: number; z: number; right: boolean; activity: HonuActivity } {
  const { leave, back } = honuHours(p, duskHour);
  const T = HONU_DAY.travelH;
  const lying = (x: number, z: number): { x: number; y: number; z: number } => ({ x, y: lyingY(floorY, x, z, size), z });
  // Its place on its way along the coast `t` seconds into its swim: between two of its points, moved out from the coast
  // where the straight line between them would break the surface.
  const phase = (t: number): number => 2 * Math.PI * (t / HONU_DRAW.wanderS + p.wander);
  const coastAt = (t: number): { x: number; z: number } => {
    const s = ((1 - Math.cos(phase(t))) / 2) * Math.max(0, p.shallows.length - 1);
    const k = Math.min(Math.floor(s), Math.max(0, p.shallows.length - 2));
    const [a, b] = [p.shallows[k] ?? { x: p.forage.x, z: p.z }, p.shallows[k + 1] ?? p.shallows[k] ?? { x: p.forage.x, z: p.z }];
    const z = a.z + (b.z - a.z) * (s - k);
    let x = a.x + (b.x - a.x) * (s - k);
    for (let i = 0; i < 200 && lyingY(floorY, x, z, size) + size.height / 2 > top; i++) x += SHALLOWS_STEP;
    return { x, z };
  };
  const here = coastAt(seconds);
  const [rest, forage] = [lying(p.rest.x, p.z), lying(here.x, here.z)];
  const ease = (t: number): number => t * t * (3 - 2 * t);
  const along = (from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, t: number): { x: number; y: number; z: number } => {
    const [xx, zz] = [from.x + (to.x - from.x) * ease(t), from.z + (to.z - from.z) * ease(t)];
    return { x: xx, z: zz, y: Math.max(from.y + (to.y - from.y) * ease(t), lyingY(floorY, xx, zz, size)) };
  };
  if (hour >= leave && hour < leave + T) return { ...along(rest, forage, (hour - leave) / T), right: forage.x > rest.x, activity: 'leaving' };
  if (hour >= back - T && hour < back) return { ...along(forage, rest, (hour - back + T) / T), right: rest.x > forage.x, activity: 'returning' };
  if (hour >= leave + T && hour < back - T) {
    // Facing the way it moves across the exhibit (the user's word, M140: "for all animals, the should face the
    // direction they are moving"): by where it was a moment before, not by its way along the coast's points, which turn
    // back on themselves where the coast does (first drawn: after, `ExhibitTurtles` turns it by its last move).
    const before = coastAt(seconds - HONU_FACING_S);
    return { ...forage, right: here.x === before.x ? Math.sin(phase(seconds)) > 0 : here.x > before.x, activity: 'foraging' };
  }
  return { ...rest, right: p.wander > 0.5, activity: 'resting' };
}

/** A honu drawn: which, its middle, its size and activity, its opacity. */
export interface HonuDrawn {
  sprite: HonuSprite;
  places: HonuPlaces;
  middle: THREE.Vector3;
  width: number;
  height: number;
  activity: HonuActivity;
  alpha: number;
}

/** The exhibit's honu: their places, their sprites as the day has them, their day and their fading. */
export class ExhibitTurtles {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects: [number, number, number, number][];
  private readonly places = new Map<string, { cm: number; island: number; places: HonuPlaces | null }>();
  private sprites: HonuSprite[] = [];
  private alphas = new Map<string, { sprite: HonuSprite; alpha: number }>();
  /**
   * Each honu's place on the floor as last drawn, the way it last moved across it, and the way it faced on the screen,
   * so it faces the way it is seen to move (M142).
   */
  private lastAt = new Map<string, { x: number; z: number }>();
  private ways = new Map<string, { x: number; z: number }>();
  private facing = new Map<string, boolean>();
  /** The camera they are seen by (`face`), null until one is given: the world's +x the screen's right. */
  private camera: THREE.Camera | null = null;
  /** The hour and the dusk they were last swum at, for `face`. */
  private day = { hour: 0, duskHour: 0 };
  /** Each honu's stroke's clock (`HONU_STROKE`): its frame the clock's whole part, turned at each whole. */
  private strokes = new Map<string, number>();
  /** The hour last swum at, for the pace it is played at; null before the first. */
  private lastHour: number | null = null;
  private seconds = 0;
  private shown: HonuDrawn[] = [];

  /** `unitsPerCm`: scene units a cm of the animal, the fish's. */
  constructor(private readonly water: HonuWater, private readonly unitsPerCm: number) {
    const { texture, rects } = fishAtlas(HONU_FRAMES.map((f) => f.sprite));
    this.rects = HONU_FRAMES.map((f) => rects.get(f.sprite) as [number, number, number, number]);
    this.batch = new FishBatch(texture);
    // Drawn over the fish beside it, under the whales.
    this.batch.mesh.renderOrder = 4;
    this.group.add(this.batch.mesh);
  }

  /** A sprite's drawn size, its carapace its length. */
  sizeOf(cm: number): { width: number; height: number } {
    const width = (cm * this.unitsPerCm * HONU_SPRITE.width) / HONU_CARAPACE_PX;
    return { width, height: (width * HONU_SPRITE.height) / HONU_SPRITE.width };
  }

  /**
   * A sprite's places, found for its length rounded up to the next cm, so they hold it, and found again only when that
   * or its island changes (a few times a year as it grows, not each day: they take some thousands of reads of the
   * floor).
   */
  private placesOf(s: HonuSprite): HonuPlaces | null {
    const key = `${s.adult ? 'a' : 'y'}${s.index}`;
    const cm = Math.ceil(s.cm);
    const was = this.places.get(key);
    if (was && was.cm === cm && was.island === s.island) return was.places;
    const places = honuPlaces(s.index * 2 + (s.adult ? 1 : 0), s.island, this.water, this.sizeOf(cm));
    this.places.set(key, { cm, island: s.island, places });
    return places;
  }

  /** The day's sprites (`honuSprites`): those beyond them fade out, those new fade in. */
  set(sprites: HonuSprite[]): void {
    this.sprites = sprites;
    for (const s of sprites) {
      const key = `${s.adult ? 'a' : 'y'}${s.index}`;
      const was = this.alphas.get(key);
      this.alphas.set(key, { sprite: s, alpha: was?.alpha ?? 0 });
    }
  }

  /** Where the day has them at `hour` (the sun's) on a day whose civil twilight ends at `duskHour`, moved on by `seconds`; still when `calm`. */
  swim(hour: number, duskHour: number, seconds: number, calm = false): void {
    if (!calm) this.seconds = (this.seconds + seconds) % 86_400;
    this.day = { hour, duskHour };
    const step = seconds / HONU_DRAW.fadeS;
    const wanted = new Set(this.sprites.map((s) => `${s.adult ? 'a' : 'y'}${s.index}`));
    for (const [key, a] of this.alphas) {
      const places = this.placesOf(a.sprite);
      a.alpha = wanted.has(key) && places ? Math.min(1, a.alpha + step) : Math.max(0, a.alpha - step);
    }
    // Each one's stroke run on by the speed it will swim over the next `aheadS` (M143), its hour run on at the pace it
    // was played this frame; its resting frame held lying at rest; still when calm. Begun at a point of its own, as the
    // whales' are (M141).
    const pace = this.lastHour === null || seconds <= 0 ? 0 : ((((hour - this.lastHour) % 24) + 24) % 24) / seconds;
    this.lastHour = hour;
    for (const [key, a] of this.alphas) {
      const places = this.placesOf(a.sprite);
      if (!places || a.alpha <= 0) continue;
      const size = this.sizeOf(a.sprite.cm);
      const at = honuAt(places, hour, duskHour, this.seconds, this.water.floorY, size, this.water.top);
      const was = this.strokes.get(key) ?? 2 * hash3(a.sprite.index, a.sprite.adult ? 1 : 0, 83);
      if (at.activity === 'resting') this.strokes.set(key, HONU_STROKE.restFrame);
      else if (calm) this.strokes.set(key, was);
      else {
        const t = HONU_STROKE.aheadS;
        const next = honuAt(places, (hour + pace * t) % 24, duskHour, this.seconds + t, this.water.floorY, size, this.water.top);
        const body = (size.width * HONU_STROKE.bodyPx) / HONU_SPRITE.width;
        const speed = Math.hypot(next.x - at.x, next.y - at.y, next.z - at.z) / t / body;
        this.strokes.set(key, (was + seconds / strokeHoldS(speed)) % 2);
      }
    }
    this.draw();
  }

  /**
   * Each turned the way `camera` sees it move across its screen (M142), by the camera as last placed, up to a frame
   * behind, as the fish are (`FishSchools.fit`).
   */
  face(camera: THREE.Camera): void {
    this.camera = camera;
    this.draw();
  }

  private draw(): void {
    const draws: FishDraw[] = [];
    const shown: HonuDrawn[] = [];
    // The screen's right and up, for a frame laid on another (`frameQuad`).
    const axes = this.camera ? { right: new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0).normalize(), up: new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1).normalize() } : WORLD_AXES;
    for (const [key, a] of this.alphas) {
      const places = this.placesOf(a.sprite);
      if (!places || a.alpha <= 0) continue;
      const size = this.sizeOf(a.sprite.cm);
      const at = honuAt(places, this.day.hour, this.day.duskHour, this.seconds, this.water.floorY, size, this.water.top);
      // Facing the way it last moved across the exhibit (the user's word, M140: "for all animals, the should face the
      // direction they are moving"), as the fish do: its way out and home runs on the observation clock's hour (M95)
      // and its wander on its own swim's seconds, at whatever pace each is played, so neither alone says which way it
      // moves on screen (MEASURED at M140, by each frame's move along x at a 5 s day: faced by them, 292 of 23,296
      // frames moved swam backward). Lying still, it keeps the way it came; first drawn, `honuAt`'s.
      // Resting, it never turns (M140's review: its resting place, found again as it grows, moves it a hair; MEASURED,
      // 0.024 to 0.030 units, each the way it came home, deeper, so none turned, but lying still is never moving).
      // That way as its camera sees it (M142): a honu lying still is seen from the other side as the camera goes round.
      const last = this.lastAt.get(key);
      const [dx, dz] = last ? [at.x - last.x, at.z - last.z] : [0, 0];
      const moving = at.activity !== 'resting' && Math.hypot(dx, dz) > 1e-9;
      const way = moving ? { x: dx, z: dz } : this.ways.get(key) ?? { x: at.right ? 1 : -1, z: 0 };
      const right = facesRight(this.camera, at, way, this.facing.get(key) ?? at.right);
      this.lastAt.set(key, { x: at.x, z: at.z });
      this.ways.set(key, way);
      this.facing.set(key, right);
      // Its stroke's frame (M143).
      const f = Math.floor(this.strokes.get(key) ?? 0) % 2;
      draws.push({ ...frameQuad(f, size, at, right, axes), rect: this.rects[f] as [number, number, number, number], right, alpha: a.alpha });
      shown.push({ sprite: a.sprite, places, middle: new THREE.Vector3(at.x, at.y, at.z), ...size, activity: at.activity, alpha: a.alpha });
    }
    this.batch.set(draws);
    this.shown = shown;
  }

  /** The light they are seen by, each channel 0 to 1, as the fish's (M96). */
  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** The water they are seen through, the floor's fog (`FishBatch.fogUniforms`). */
  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  /** The honu under a point on the screen, within its sprite's box or `withinPx` of its middle, the nearest; null for none. */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HonuSprite | null {
    const at = new THREE.Vector3();
    const end = new THREE.Vector3();
    let best = Infinity;
    let found: HonuSprite | null = null;
    for (const h of this.shown) {
      if (h.alpha < 0.5) continue;
      at.copy(h.middle).project(camera);
      if (at.z < -1 || at.z > 1) continue;
      end.copy(h.middle).add(new THREE.Vector3(h.width / 2, h.height / 2, 0)).project(camera);
      // None faded out by its nearness to the camera (`NEAR_FADE`, M105's review).
      if (nearFade(Math.abs(end.x - at.x) * widthPx, widthPx) < 0.5) continue;
      const [dx, dy] = [(Math.abs(at.x - x) * widthPx) / 2, (Math.abs(at.y - y) * heightPx) / 2];
      const inside = dx <= Math.max(withinPx, (Math.abs(end.x - at.x) * widthPx) / 2) && dy <= Math.max(withinPx, (Math.abs(end.y - at.y) * heightPx) / 2);
      if (inside && Math.hypot(dx, dy) < best) {
        best = Math.hypot(dx, dy);
        found = h.sprite;
      }
    }
    return found;
  }

  /** A honu as drawn now, by its stage and place among its stage's sprites, or null where it is not drawn. */
  at(adult: boolean, index: number): HonuDrawn | null {
    return this.shown.find((h) => h.sprite.adult === adult && h.sprite.index === index) ?? null;
  }

  /** The sprites the day has, young and adults, for the card. */
  counts(): { young: number; adults: number } {
    return { young: this.sprites.filter((s) => !s.adult).length, adults: this.sprites.filter((s) => s.adult).length };
  }

  /** The honu drawn now, and what the batch draws, for the tests. */
  drawn(): HonuDrawn[] {
    return this.shown;
  }

  draws(): FishDraw[] {
    return this.batch.drawn();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
